/**
 * Referral Rewards Block Component
 * 
 * Shows pending and claimed referral rewards.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Gift, Loader2, ChevronDown, ChevronUp, Download, DollarSign, Check } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { apiClient } from '../../lib/api/client'
import { toast } from '../../hooks/use-toast'
import type { UserReward } from '../../types'

interface ReferralRewardsBlockProps {
  rewards: UserReward[]
  onRewardClaimed: () => void
}

export function ReferralRewardsBlock({ rewards, onRewardClaimed }: ReferralRewardsBlockProps) {
  const { t } = useTranslation('common')
  const [showClaimed, setShowClaimed] = useState(false)
  const [claiming, setClaiming] = useState<number | null>(null)

  const pendingRewards = rewards.filter(r => r.status === 'pending')
  const claimedRewards = rewards.filter(r => r.status === 'claimed')

  const handleClaim = async (userRewardId: number) => {
    try {
      setClaiming(userRewardId)
      const result = await apiClient.rewards.claimReward(userRewardId)
      
      if (result.success) {
        toast({
          title: t('rewards.rewardClaimed', 'Reward claimed!'),
          description: result.message,
        })
        onRewardClaimed()
      } else {
        toast({
          title: t('rewards.claimFailed', 'Claim failed'),
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: t('rewards.claimFailed', 'Claim failed'),
        description: error.message || t('errors.generic', 'Something went wrong'),
        variant: 'destructive',
      })
    } finally {
      setClaiming(null)
    }
  }

  const getRewardIcon = (rewardType: string | undefined) => {
    if (rewardType === 'bonus_downloads') {
      return <Download className="h-4 w-4" />
    }
    return <DollarSign className="h-4 w-4" />
  }

  const getRewardValue = (reward: UserReward) => {
    if (reward.reward?.reward_type === 'bonus_downloads') {
      return `+${reward.reward.bonus_download_amount} ${t('referrals.takes', 'takes')}`
    }
    return `+$${reward.reward?.credit_amount_usd.toFixed(2)}`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <Card className="referral-rewards-block">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-purple-500" />
          {t('referrals.referralRewards', 'Referral Rewards')}
          {pendingRewards.length > 0 && (
            <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30 ml-2">
              {pendingRewards.length} {t('referrals.pending', 'pending')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Pending rewards */}
        {pendingRewards.length > 0 ? (
          <div className="pending-rewards space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              {t('referrals.pendingRewards', 'Pending rewards')}
            </h4>
            {pendingRewards.map(reward => (
              <div 
                key={reward.id}
                className="reward-item flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-500/20"
              >
                <div className="reward-info flex items-center gap-3">
                  <div className="icon-wrapper p-2 rounded-lg bg-purple-500/10">
                    {getRewardIcon(reward.reward?.reward_type)}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{reward.reward?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {reward.reward?.description}
                    </p>
                  </div>
                </div>
                <div className="reward-actions flex items-center gap-3">
                  <span className="reward-value text-sm font-bold text-purple-600">
                    {getRewardValue(reward)}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleClaim(reward.id)}
                    disabled={claiming !== null}
                    className="claim-btn bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    {claiming === reward.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('rewards.claim', 'Claim')
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-pending text-center py-4">
            <p className="text-sm text-muted-foreground">
              {t('referrals.noPendingRewards', 'No pending rewards. Invite more friends to earn!')}
            </p>
          </div>
        )}

        {/* Claimed rewards (collapsible) */}
        {claimedRewards.length > 0 && (
          <div className="claimed-rewards pt-4 border-t border-border">
            <button
              onClick={() => setShowClaimed(!showClaimed)}
              className="toggle-claimed w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                {t('referrals.claimedRewards', 'Claimed rewards')} ({claimedRewards.length})
              </span>
              {showClaimed ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            
            {showClaimed && (
              <div className="claimed-list mt-3 space-y-2">
                {claimedRewards.map(reward => (
                  <div 
                    key={reward.id}
                    className="claimed-item flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                  >
                    <div className="flex items-center gap-2">
                      <div className="icon-wrapper p-1.5 rounded bg-emerald-500/10">
                        {getRewardIcon(reward.reward?.reward_type)}
                      </div>
                      <span className="text-sm">{reward.reward?.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="text-emerald-600 font-medium">
                        {getRewardValue(reward)}
                      </span>
                      {reward.claimed_at && (
                        <span className="hidden sm:inline">
                          • {formatDate(reward.claimed_at)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

